import { Component, Input, ElementRef, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { System } from 'ngx-core-services';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatListModule } from '@angular/material/list';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { PlantCardOverviewModule } from 'ngx-zcs-blocks';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';

import { checkForChangesInSystems } from './plant-card-list.utils';

const MAX_DISPLAYED_SYSTEMS = Math.ceil(window.innerHeight / 150);
const trackByFn = (_: number, system: any): System => system.uid;

@Component({
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatListModule,
    PlantCardOverviewModule,
    RouterModule,
    TranslateModule,
  ],
  selector: 'customer-plant-card-list',
  templateUrl: './plant-card-list.component.html',
  styleUrls: ['./plant-card-list.component.scss'],
})
export class PlantCardListComponent implements AfterViewInit, OnChanges {
  @ViewChild('anchor') anchor?: ElementRef;

  private readonly _systems$ = new BehaviorSubject<System[] | undefined | null>(undefined);
  @Input() set systems(value: System[] | undefined | null) {
    /**
     * Check if the new value is different from the current one
     */
    const hasChanges = checkForChangesInSystems(this._systems$.value, value);
    this.noSystem = value?.length === 0;
    if (hasChanges) {
      this._systems$.next(value);
      this.resetDisplayedSystems();
    }
  }

  get systems(): System[] | undefined | null {
    return this._systems$.value;
  }

  @Input() refresh$ = new Subject<void>();

  noSystem?: boolean;
  displayedSystems: System[] = [];
  private observer?: IntersectionObserver;

  readonly trackByFn = trackByFn;

  constructor(private readonly host: ElementRef) {}

  ngAfterViewInit(): void {
    this.initObserver();
  }

  ngOnChanges(changes: SimpleChanges): void {
    /**
     * Check if the 'systems' input has changed
     */
    if (changes.systems) {
      const prevSystems: System[] | undefined | null = changes.systems.previousValue;
      const currSystems: System[] | undefined | null = changes.systems.currentValue;

      /**
       * Check if there are significant changes in the 'systems' input
       */
      const hasChanges = checkForChangesInSystems(prevSystems, currSystems);

      if (hasChanges) {
        this.resetDisplayedSystems();

        /**
         * Disconnect the observer to avoid multiple observers on the same element
         * and reinitialize it
         */
        this.observer?.disconnect();
        this.initObserver();
      }
    }
  }

  private initObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          this.loadMoreSystems();
        }
      },
      {
        root: this.host.nativeElement,
        threshold: 0.1,
      },
    );

    if (this.anchor) {
      this.observer.observe(this.anchor.nativeElement);
    }
  }

  private resetDisplayedSystems(): void {
    this.displayedSystems = this.systems?.slice(0, MAX_DISPLAYED_SYSTEMS) || [];
  }

  loadMoreSystems(): void {
    const currentLength = this.displayedSystems.length;
    const moreSystems = this.systems?.slice(currentLength, currentLength + MAX_DISPLAYED_SYSTEMS);
    if (moreSystems && moreSystems.length) {
      this.displayedSystems = [...this.displayedSystems, ...moreSystems];
    }
  }
}
